import { supabase } from '../lib/supabase'
import { logger } from '../utils/logger'
import { timed_select } from '../utils/debug_perf'

export const artworksService = {
  // Get all published artworks
  async getArtworks(filters = {}) {
    try {
      const result = await timed_select('getArtworks', () => {
        let query = supabase
          .from('artworks')
          .select(`
            id,
            title,
            price,
            artist_id,
            category_id,
            images,
            status,
            created_at,
            artist:users!artist_id(
              id,
              full_name,
              profile_image_url
            ),
            category:categories!category_id(
              id,
              name,
              slug
            ),
            likes(count),
            comments(count)
          `)
          .eq('status', 'published')
          .order('created_at', { ascending: false })

        if (filters.category_id) {
          query = query.eq('category_id', filters.category_id)
        }

        if (filters.artist_id) {
          query = query.eq('artist_id', filters.artist_id)
        }

        if (filters.limit) {
          query = query.limit(filters.limit)
        }

        return query
      });

      const { data, error } = result;
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get featured artworks (most liked/viewed)
  async getFeaturedArtworks(limit = 12) {
    try {
      const result = await timed_select('getFeaturedArtworks', () =>
        supabase
          .from('artworks')
          .select(`
            id,
            title,
            price,
            artist_id,
            category_id,
            images,
            status,
            views,
            created_at,
            artist:users!artist_id(
              id,
              full_name,
              profile_image_url
            ),
            category:categories!category_id(
              id,
              name,
              slug
            ),
            likes(count),
            comments(count)
          `)
          .eq('status', 'published')
          .order('views', { ascending: false })
          .limit(limit)
      );

      const { data, error } = result;
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get artwork by ID
  async getArtworkById(id) {
    try {
      const result = await timed_select('getArtworkById', () =>
        supabase
          .from('artworks')
          .select(`
            id,
            title,
            description,
            price,
            artist_id,
            category_id,
            images,
            dimensions,
            weight,
            materials,
            inspiration,
            status,
            is_unique,
            views,
            created_at,
            artist:users!artist_id(
              id,
              full_name,
              profile_image_url,
              bio,
              location
            ),
            category:categories!category_id(
              id,
              name,
              slug
            ),
            likes(count),
            comments(
              id,
              content,
              created_at,
              user:users(
                id,
                full_name,
                profile_image_url
              ),
              comment_replies(
                id,
                content,
                created_at,
                user:users(
                  id,
                  full_name,
                  profile_image_url
                )
              )
            )
          `)
          .eq('id', id)
          .single()
      );

      const { data, error } = result;
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Search artworks
  async searchArtworks(query, filters = {}) {
    try {
      const result = await timed_select('searchArtworks', () => {
        let searchQuery = supabase
          .from('artworks')
          .select(`
            id,
            title,
            price,
            artist_id,
            category_id,
            images,
            status,
            created_at,
            artist:users!artist_id(
              id,
              full_name,
              profile_image_url
            ),
            category:categories!category_id(
              id,
              name,
              slug
            ),
            likes(count),
            comments(count)
          `)
          .eq('status', 'published')
          .ilike('title', `%${query}%`)
          .order('created_at', { ascending: false })

        if (filters.category_id) {
          searchQuery = searchQuery.eq('category_id', filters.category_id)
        }

        if (filters.limit) {
          searchQuery = searchQuery.limit(filters.limit)
        }

        return searchQuery
      });

      const { data, error } = result;
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get related artworks (same category, different artist)
  async getRelatedArtworks(artwork_id, category_id, limit = 6) {
    try {
      const result = await timed_select('getRelatedArtworks', () =>
        supabase
          .from('artworks')
          .select(`
            id,
            title,
            price,
            artist_id,
            category_id,
            images,
            status,
            created_at,
            artist:users!artist_id(
              id,
              full_name,
              profile_image_url
            ),
            category:categories!category_id(
              id,
              name,
              slug
            )
          `)
          .eq('status', 'published')
          .eq('category_id', category_id)
          .neq('id', artwork_id)
          .limit(limit)
          .order('views', { ascending: false })
      );

      const { data, error } = result;
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get artworks by category
  async getArtworksByCategory(category_id) {
    try {
      const result = await timed_select(`getArtworksByCategory[${category_id}]`, () =>
        supabase
          .from('artworks')
          .select(`
            id,
            title,
            price,
            artist_id,
            category_id,
            images,
            status,
            created_at,
            artist:users!artist_id(
              id,
              full_name,
              profile_image_url
            ),
            category:categories!category_id(
              id,
              name,
              slug
            ),
            likes(count),
            comments(count)
          `)
          .eq('status', 'published')
          .eq('category_id', category_id)
          .order('created_at', { ascending: false })
      );

      const { data, error } = result;
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get artworks by artist (all status - for MyArtworks page)
  async getArtworksByArtist(artist_id) {
    try {
      const result = await timed_select('getArtworksByArtist', () =>
        supabase
          .from('artworks')
          .select(`
            id,
            title,
            description,
            inspiration,
            price,
            artist_id,
            category_id,
            images,
            status,
            is_unique,
            created_at,
            artist:users!artist_id(
              id,
              full_name,
              profile_image_url
            ),
            category:categories!category_id(
              id,
              name,
              slug
            ),
            likes(count),
            comments(count)
          `)
          .eq('artist_id', artist_id)
          .order('created_at', { ascending: false })
      );

      const { data, error } = result;
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Increment artwork views
  async incrementViews(artwork_id) {
    try {
      const { error } = await supabase.rpc('increment_artwork_views', {
        artwork_id: artwork_id
      })

      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: error.message }
    }
  },

  // Create new artwork
  async createArtwork(artwork_data, image_files) {
    try {
      logger.info('ArtworksService', 'createArtwork called', {
        artwork_id: artwork_data.id,
        has_images: !!(artwork_data.images && artwork_data.images.length > 0),
        images_count: artwork_data.images?.length || 0,
        images: artwork_data.images,
        has_image_files: !!(image_files && image_files.length > 0),
      });

      // Handle images: either from uploaded URLs or upload files
      let image_urls = []

      // If artwork_data.images exists (new flow), use those URLs
      if (artwork_data.images && artwork_data.images.length > 0) {
        image_urls = artwork_data.images
        logger.debug('ArtworksService', 'Using uploaded URLs (new flow)', {
          image_urls_count: image_urls.length,
          image_urls,
        });
      }
      // Otherwise, upload image_files (legacy flow)
      else if (image_files && image_files.length > 0) {
        for (let i = 0; i < image_files.length; i++) {
          const file = image_files[i]
          const file_ext = file.name.split('.').pop()
          const file_name = `${Date.now()}-${i}.${file_ext}`
          const file_path = `artworks/${file_name}`

          const { error: upload_error } = await supabase.storage
            .from('artwork-images')
            .upload(file_path, file, {
              cacheControl: '3600',
              upsert: false
            })

          if (upload_error) throw upload_error

          // Get public URL
          const { data: { publicUrl: public_url } } = supabase.storage
            .from('artwork-images')
            .getPublicUrl(file_path)

          image_urls.push(public_url)
        }
      }

      // Prepare artwork data with auto-assignment of declared_value = price
      const price = parseFloat(artwork_data.price);
      const artwork_data_for_db = {
        artist_id: artwork_data.artist_id,
        category_id: artwork_data.category_id,
        title: artwork_data.title,
        description: artwork_data.description,
        price: price,
        dimensions: {
          length: parseFloat(artwork_data.length),
          width: parseFloat(artwork_data.width),
          height: parseFloat(artwork_data.height)
        },
        weight: parseFloat(artwork_data.weight),
        declared_value: price, // AUTO-ASSIGNMENT: declared_value = price (industry standard)
        materials: artwork_data.materials
          ? artwork_data.materials.split(',').map(m => m.trim()).filter(m => m.length > 0)
          : [],
        images: image_urls,
        inspiration: artwork_data.inspiration,
        is_fragile: artwork_data.is_fragile || false, // Now correctly mapped from isFragile
        is_unique: artwork_data.is_unique !== undefined ? artwork_data.is_unique : true, // Default true if not provided
        shipping_methods: artwork_data.shipping_methods || ['pickup', 'delivery'],
        status: 'published', // ✅ Direct to published (MVP - no moderation needed for solo developer)
        shipping_dimensions: {
          length: parseFloat(artwork_data.length),
          width: parseFloat(artwork_data.width),
          height: parseFloat(artwork_data.height)
        },
        shipping_weight: parseFloat(artwork_data.weight) * 1000 // Convert kg to grams
      }

      logger.info('ArtworksService', 'Inserting artwork into Supabase', {
        images_in_data: image_urls,
        images_count_in_data: image_urls.length,
        artwork_data_for_db_images: artwork_data_for_db.images,
        artwork_data_for_db,
      });

      // Insert artwork into database
      const { data, error } = await supabase
        .from('artworks')
        .insert([artwork_data_for_db])
        .select()
        .single()

      logger.info('ArtworksService', 'Supabase insert result', {
        success: !error,
        error,
        data_images: data?.images,
        data,
      });

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get artworks paginated (Supabase .range() + { count: 'exact' })
  async getArtworksPaginated(page = 1, limit = 20, filters = {}) {
    try {
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      const result = await timed_select(
        `getArtworksPaginated[p${page}${filters.category_id ? `,cat${filters.category_id}` : ''}]`,
        () => {
          let query = supabase
            .from('artworks')
            .select(`
              id,
              title,
              price,
              artist_id,
              category_id,
              images,
              status,
              created_at,
              artist:users!artist_id(
                id,
                full_name,
                profile_image_url
              ),
              category:categories!category_id(
                id,
                name,
                slug
              ),
              likes(count),
              comments(count)
            `, { count: 'exact' })
            .eq('status', 'published')
            .order('created_at', { ascending: false })
            .range(from, to);

          if (filters.category_id) {
            query = query.eq('category_id', filters.category_id);
          }

          return query;
        }
      );

      const { data, count, error } = result;
      if (error) throw error;
      return { data, count, error: null };
    } catch (error) {
      return { data: null, count: 0, error: error.message };
    }
  },

  /**
   * Update an existing artwork
   * @param {string} artwork_id - UUID of the artwork to update
   * @param {Object} update_data - Fields to update
   * @returns {Promise<{data: Object|null, error: string|null}>}
   */
  async updateArtwork(artwork_id, update_data) {
    try {
      logger.info('ArtworksService', 'updateArtwork called', {
        artwork_id,
        fields_to_update: Object.keys(update_data)
      });

      // Prepare update data with correct snake_case mapping
      const artwork_update_for_db = {};

      // Only include fields that are present in update_data
      if (update_data.title !== undefined) artwork_update_for_db.title = update_data.title;
      if (update_data.description !== undefined) artwork_update_for_db.description = update_data.description;
      if (update_data.price !== undefined) {
        const price = parseFloat(update_data.price);
        artwork_update_for_db.price = price;
        artwork_update_for_db.declared_value = price; // Keep declared_value synced with price
      }
      if (update_data.category_id !== undefined) artwork_update_for_db.category_id = update_data.category_id;
      if (update_data.status !== undefined) artwork_update_for_db.status = update_data.status;
      if (update_data.is_unique !== undefined) artwork_update_for_db.is_unique = update_data.is_unique;
      if (update_data.is_fragile !== undefined) artwork_update_for_db.is_fragile = update_data.is_fragile;
      if (update_data.package_type !== undefined) artwork_update_for_db.package_type = update_data.package_type;
      if (update_data.inspiration !== undefined) artwork_update_for_db.inspiration = update_data.inspiration;

      // Handle dimensions (if any dimension is updated, update all three)
      if (update_data.length !== undefined || update_data.width !== undefined || update_data.height !== undefined) {
        artwork_update_for_db.dimensions = {
          length: update_data.length !== undefined ? parseFloat(update_data.length) : undefined,
          width: update_data.width !== undefined ? parseFloat(update_data.width) : undefined,
          height: update_data.height !== undefined ? parseFloat(update_data.height) : undefined
        };
        // Also update shipping_dimensions
        artwork_update_for_db.shipping_dimensions = artwork_update_for_db.dimensions;
      }

      // Handle weight
      if (update_data.weight !== undefined) {
        artwork_update_for_db.weight = parseFloat(update_data.weight);
        artwork_update_for_db.shipping_weight = parseFloat(update_data.weight) * 1000; // kg to grams
      }

      // Handle materials (convert comma-separated string to array)
      if (update_data.materials !== undefined) {
        artwork_update_for_db.materials = typeof update_data.materials === 'string'
          ? update_data.materials.split(',').map(m => m.trim()).filter(m => m.length > 0)
          : update_data.materials;
      }

      // Add updated_at timestamp
      artwork_update_for_db.updated_at = new Date().toISOString();

      logger.info('ArtworksService', 'Updating artwork in Supabase', {
        artwork_id,
        artwork_update_for_db
      });

      // Update artwork in database
      const { data, error } = await supabase
        .from('artworks')
        .update(artwork_update_for_db)
        .eq('id', artwork_id)
        .select()
        .single();

      logger.info('ArtworksService', 'Supabase update result', {
        success: !error,
        error,
        data
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('ArtworksService', 'updateArtwork error', { error: error.message });
      return { data: null, error: error.message };
    }
  }
}